import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useOrganization } from "@/contexts/organization-context";
import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import {
  QUERY_RETRY,
  QUERY_STALE_TIME,
  getRetryDelay,
} from "@/lib/constants/query-config";

import { buildPreferencesApi, fetchPreferences } from "./api";
import type { PreferencesBundle } from "./types";

export function usePreferencesQuery() {
  const { getToken, isSignedIn, isLoaded } = useAuthSafe();
  const { organizationId, isLoading: orgLoading } = useOrganization();

  const api = useMemo(() => {
    return buildPreferencesApi(getToken, () => organizationId ?? undefined);
  }, [getToken, organizationId]);

  const query = useQuery<Partial<PreferencesBundle>>({
    queryKey: ["user-preferences", organizationId],
    enabled: isLoaded && isSignedIn && Boolean(organizationId) && !orgLoading,
    staleTime: QUERY_STALE_TIME.VERY_STABLE,
    retry: QUERY_RETRY.ONCE,
    retryDelay: getRetryDelay,
    queryFn: () => fetchPreferences(api),
  });

  return {
    ...query,
    api,
    organizationId,
    isAuthReady: isLoaded && isSignedIn,
  };
}
